import { formatCurrencyINR } from "@utils/numberFormatter";

interface PricingComponents {
  [key: string]: number | string;
}

export interface AdditionalAttributes {
  kind_offers: string;
  waiting_period?: string;
  delivery_days?: string;
}
export interface DiscountComponents {
  ackodiscount: string;
  newyear_discount: string;
  consumerdiscount: string;
  special_discount: string;
  ripdiscount: string;
}
export interface PriceBreakup {
  id: string;
  ex_showroom_price: number;
  pricing_components: PricingComponents;
  onroad_discount_components: any;
  discount_components: DiscountComponents;
  additional_attributes: AdditionalAttributes;
  booking_components: Record<string, unknown>;
  market_price: string;
  onroad_price: string;
  acko_drive_price: string;
  acko_drive_savings: string;
}
interface PriceData {
  pricing_components?: PricingComponents;
  discount_components?: PricingComponents;
  ex_showroom_price?: number | string;
  market_price?: number | string;
  onroad_price?: number | string;
  acko_drive_price?: number | string;
  acko_drive_savings?: number | string;
}

  const componentLabels: Record<string, string> = {
    ex_showroom: "Ex-showroom price",
    "Ex-showroom": "Ex-showroom price",
    registration_amount: "Registration amount",
    "Registration amount": "Registration amount",
    dealer_insurance_premium: "Dealer insurance premium",
    "Dealer insurance premium": "Dealer insurance premium",
    tax_collected_at_source: "Tax collected at source",
    "Tax collected at source": "Tax collected at source",
    hypothecation_charges: "Hypothecation",
    "Hypothecation charges": "Hypothecation",
    fast_tag: "FASTag",
    Fastag: "FASTag",
    "HSRP number plate charges": "HSRP number plate charges",
    mcd_charges: "MCD charge",
    "MCD charge": "MCD charge",
    auto_card: "Auto card",
    "Auto card": "Auto card",
    basic_accessories: "Basic accessories",
    "Basic accessories": "Basic accessories",
  };

const getValue = (breakUp: any, value: string): string | null =>
  breakUp[value] ? formatCurrencyINR(breakUp[value]) : null;

export const chargesCalculationV1 = (priceData: PriceData | any): { label: string; value: string | null }[] => {
  if (priceData?.price?.price_breakup) {
    const priceBreakup = priceData.price.price_breakup || priceData.pricing_components;
    return [
      {
        label: "Ex-showroom price",
        value: getValue(priceBreakup, "ex_showroom"),
      },
      {
        label: "Registration amount",
        value: getValue(priceBreakup, "registration_amount"),
      },
      {
        label: "Dealer insurance premium",
        value: getValue(priceBreakup, "dealer_insurance_premium"),
      },
      {
        label: "Tax",
        value: getValue(priceBreakup, "tax_collected_at_source"),
      },
      {
        label: "Hypothecation",
        value: getValue(priceBreakup, "hypothecation_charges"),
      },
      {
        label: "Mcd Charges",
        value: getValue(priceBreakup, "mcd_charges"),
      },
      {
        label: "Auto Card",
        value: getValue(priceBreakup, "auto_card"),
      },
      {
        label: "FASTag",
        value: getValue(priceBreakup, "fast_tag"),
      },
      {
        label: "Basic Accessories",
        value: getValue(priceBreakup, "basic_accessories"),
      },
    ];
  }

  if (!priceData?.pricing_components) {
    return [];
  }

  const pricingComponents = priceData.pricing_components;
  const charges: { label: string; value: string | null }[] = [];

  const exShowroomPrice =
    priceData.ex_showroom_price ||
    pricingComponents.ex_showroom ||
    pricingComponents["Ex-showroom"];

  if (exShowroomPrice) {
    charges.push({
      label: "Ex-showroom price",
      value: formatCurrencyINR(exShowroomPrice),
    });
  }

  Object.entries(pricingComponents).forEach(([key, value]) => {
    if (
      (key === "ex_showroom" || key === "Ex-showroom") &&
      exShowroomPrice
    ) {
      return;
    }

    if (value === null || value === undefined) {
      return;
    }
    const numValue = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(numValue) || numValue === 0) {
      return;
    }

    const label = componentLabels[key] || key;
    const formattedValue = formatCurrencyINR(numValue);
    if (formattedValue && formattedValue !== "₹0") {
      charges.push({
        label,
        value: formattedValue,
      });
    }
  });

  return charges;
};

export const chargesCalculationV2 = (priceData: PriceBreakup): { label: string; value: string | null }[] => {
  const pricingComponents = priceData.pricing_components;
  const charges: { label: string; value: string | null }[] = [];

  Object.keys(componentLabels).forEach((key) => {
    if (!(key in pricingComponents)) {
      return;
    }

    const value = pricingComponents[key as keyof typeof pricingComponents];

    if (value === null || value === undefined) {
      return;
    }

    const label = componentLabels[key];
    const formattedValue = formatCurrencyINR(Number(value));
    if (formattedValue && formattedValue !== "₹0") {
      charges.push({
        label,
        value: formattedValue,
      });
    }
  });

  return charges;
};